import { mean, percentile } from './computeSummaryStats';
import { DailyDiscountStats, DailyMarketStats, ParsedOtaCsv } from './types';

export interface ScrapePrvMonthRow {
  label: string;
  medianPrvPct: number | null;
  avgOta: number | null;
  lowOta: number | null;
  leSell: number | null;
}

export interface ScrapePrvDowRow {
  label: string;
  medianPrvPct: number | null;
  avgOta: number | null;
  leSell: number | null;
}

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0]; // Mon..Sun, matching the mockup's row order

function median(values: ReadonlyArray<number>): number | null {
  return percentile(values, 0.5);
}

function monthLabel(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  const month = d.toLocaleDateString('en-AU', { month: 'short' });
  const year = String(d.getFullYear()).slice(-2);
  return `${month}-${year}`;
}

function monthKey(date: string): string {
  return date.slice(0, 7); // YYYY-MM, sorts chronologically
}

/** Per-calendar-month PRV/OTA snapshot, grouping every scraped date by its stay-start month. */
export function computeScrapePrvByMonth(
  parsed: ParsedOtaCsv,
  marketStats: Array<DailyMarketStats>,
  discountStats: Array<DailyDiscountStats>,
): Array<ScrapePrvMonthRow> {
  const marketByDate = new Map(marketStats.map((m) => [m.date, m]));
  const discountByDate = new Map(discountStats.map((d) => [d.date, d]));

  const groups = new Map<
    string,
    { label: string; prv: Array<number>; ota: Array<number>; low: Array<number>; sell: Array<number> }
  >();

  for (const row of parsed.rows) {
    const key = monthKey(row.date);
    if (!groups.has(key)) {
      groups.set(key, { label: monthLabel(row.date), prv: [], ota: [], low: [], sell: [] });
    }
    const group = groups.get(key);

    for (const value of Object.values(row.otaRates)) {
      if (value != null) {
        group.ota.push(value);
      }
    }

    const market = marketByDate.get(row.date);
    if (market?.low != null) {
      group.low.push(market.low);
    }

    const discount = discountByDate.get(row.date);
    if (discount?.genDiscountPct != null) {
      group.prv.push(discount.genDiscountPct);
    }
    if (discount?.sellDisplay != null && discount.sellDisplay > 0) {
      group.sell.push(discount.sellDisplay);
    }
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, group]) => ({
      label: group.label,
      medianPrvPct: median(group.prv),
      avgOta: mean(group.ota),
      lowOta: group.low.length ? Math.min(...group.low) : null,
      leSell: mean(group.sell),
    }));
}

/** PRV/OTA snapshot grouped by day-of-week across every scraped date, Mon..Sun. */
export function computeScrapePrvByDow(
  parsed: ParsedOtaCsv,
  discountStats: Array<DailyDiscountStats>,
): Array<ScrapePrvDowRow> {
  const discountByDate = new Map(discountStats.map((d) => [d.date, d]));

  const groups = new Map<number, { prv: Array<number>; ota: Array<number>; sell: Array<number> }>();
  for (const dow of DOW_ORDER) {
    groups.set(dow, { prv: [], ota: [], sell: [] });
  }

  for (const row of parsed.rows) {
    const dow = new Date(`${row.date}T00:00:00`).getDay();
    const group = groups.get(dow);
    if (!group) {
      continue;
    }

    for (const value of Object.values(row.otaRates)) {
      if (value != null) {
        group.ota.push(value);
      }
    }

    const discount = discountByDate.get(row.date);
    if (discount?.genDiscountPct != null) {
      group.prv.push(discount.genDiscountPct);
    }
    if (discount?.sellDisplay != null && discount.sellDisplay > 0) {
      group.sell.push(discount.sellDisplay);
    }
  }

  return DOW_ORDER.map((dow) => {
    const group = groups.get(dow);
    return {
      label: DOW_LABELS[dow],
      medianPrvPct: median(group.prv),
      avgOta: mean(group.ota),
      leSell: mean(group.sell),
    };
  });
}
